'use client';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Fragment, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { AiFillInstagram, AiFillYoutube } from 'react-icons/ai';
import { FaFacebookSquare } from 'react-icons/fa';
import { FaSquareXTwitter } from 'react-icons/fa6';
import { IoLogoLinkedin } from 'react-icons/io5';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

// Estilos escopados em .cf-wrap (cores do Trakd, sem depender de tokens globais).
const STYLES = `
.cf-wrap {
  --background:#0F1219;
  --foreground:#DEDBF6;
  --primary:#9061FF;
  --secondary:#9F11CE;
  --muted-foreground:#8B88A8;
  --border:rgba(222,219,246,.12);
  --destructive:#FF5C7A;
  --pill-bg-1: color-mix(in oklch, var(--foreground) 3%, transparent);
  --pill-bg-2: color-mix(in oklch, var(--foreground) 1%, transparent);
  --pill-shadow: color-mix(in oklch, var(--background) 50%, transparent);
  --pill-highlight: color-mix(in oklch, var(--foreground) 10%, transparent);
  --pill-inset-shadow: color-mix(in oklch, var(--background) 80%, transparent);
  --pill-border: color-mix(in oklch, var(--foreground) 8%, transparent);
  --pill-bg-1-hover: color-mix(in oklch, var(--foreground) 8%, transparent);
  --pill-bg-2-hover: color-mix(in oklch, var(--foreground) 2%, transparent);
  --pill-border-hover: color-mix(in oklch, var(--foreground) 20%, transparent);
  --pill-shadow-hover: color-mix(in oklch, var(--background) 70%, transparent);
  --pill-highlight-hover: color-mix(in oklch, var(--foreground) 20%, transparent);
}
@keyframes cf-breathe {0%{transform:translate(-50%,-50%) scale(1);opacity:.6;}100%{transform:translate(-50%,-50%) scale(1.1);opacity:1;}}
@keyframes cf-marquee {from{transform:translateX(0);}to{transform:translateX(-50%);}}
@keyframes cf-heartbeat {0%,100%{transform:scale(1);filter:drop-shadow(0 0 5px color-mix(in oklch,var(--destructive) 50%,transparent));}15%,45%{transform:scale(1.2);filter:drop-shadow(0 0 10px color-mix(in oklch,var(--destructive) 80%,transparent));}30%{transform:scale(1);}}
.cf-breathe{animation:cf-breathe 8s ease-in-out infinite alternate;}
.cf-marquee{animation:cf-marquee 40s linear infinite;}
.cf-heartbeat{animation:cf-heartbeat 2s cubic-bezier(.25,1,.5,1) infinite;}
.cf-grid{ position:absolute; inset:0; z-index:0; pointer-events:none; background-size:60px 60px; background-image:linear-gradient(to right, color-mix(in oklch, var(--foreground) 3%, transparent) 1px, transparent 1px), linear-gradient(to bottom, color-mix(in oklch, var(--foreground) 3%, transparent) 1px, transparent 1px); -webkit-mask-image:linear-gradient(to bottom, transparent, black 30%, black 70%, transparent); mask-image:linear-gradient(to bottom, transparent, black 30%, black 70%, transparent); }
.cf-aurora{ position:absolute; left:50%; top:50%; height:60vh; width:80vw; transform:translate(-50%,-50%); border-radius:50%; filter:blur(80px); pointer-events:none; z-index:0; background:radial-gradient(circle at 50% 50%, color-mix(in oklch, var(--primary) 18%, transparent) 0%, color-mix(in oklch, var(--secondary) 16%, transparent) 40%, transparent 70%); }
.cf-pill{ background:linear-gradient(145deg, var(--pill-bg-1) 0%, var(--pill-bg-2) 100%); box-shadow:0 10px 30px -10px var(--pill-shadow), inset 0 1px 1px var(--pill-highlight), inset 0 -1px 2px var(--pill-inset-shadow); border:1px solid var(--pill-border); backdrop-filter:blur(16px); -webkit-backdrop-filter:blur(16px); transition:all .4s cubic-bezier(.16,1,.3,1); cursor:pointer; text-decoration:none; }
.cf-pill:hover{ background:linear-gradient(145deg, var(--pill-bg-1-hover) 0%, var(--pill-bg-2-hover) 100%); border-color:var(--pill-border-hover); box-shadow:0 20px 40px -10px var(--pill-shadow-hover), inset 0 1px 1px var(--pill-highlight-hover); color:var(--foreground); }
.cf-giant{ position:absolute; bottom:-5vh; left:0; width:100%; text-align:center; white-space:nowrap; pointer-events:none; user-select:none; z-index:0; font-size:26vw; line-height:.75; font-weight:900; letter-spacing:-.05em; color:transparent; -webkit-text-stroke:1px color-mix(in oklch, var(--foreground) 6%, transparent); background:linear-gradient(180deg, color-mix(in oklch, var(--primary) 22%, transparent) 0%, transparent 62%); -webkit-background-clip:text; background-clip:text; }
.cf-glow{ background:linear-gradient(180deg, var(--foreground) 0%, color-mix(in oklch, var(--foreground) 40%, transparent) 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; filter:drop-shadow(0 0 20px color-mix(in oklch, var(--primary) 22%, transparent)); }
.cf-footer{ position:fixed; bottom:0; left:0; height:100vh; width:100%; display:flex; flex-direction:column; justify-content:space-between; overflow:hidden; background:var(--background); color:var(--foreground); font-family:'Sora',sans-serif; }
.cf-curtain{ position:relative; height:100vh; width:100%; clip-path:polygon(0 0,100% 0,100% 100%,0 100%); }
.cf-marqueewrap{ position:absolute; top:48px; left:0; width:100%; overflow:hidden; border-top:1px solid var(--border); border-bottom:1px solid var(--border); background:color-mix(in oklch, var(--background) 60%, transparent); backdrop-filter:blur(10px); padding:16px 0; transform:rotate(-2deg) scale(1.1); box-shadow:0 25px 50px -12px rgba(0,0,0,.5); z-index:10; }
.cf-marqueerow{ display:flex; width:max-content; font-size:13px; font-weight:700; letter-spacing:.3em; text-transform:uppercase; color:var(--muted-foreground); }
.cf-mq{ display:flex; align-items:center; gap:48px; padding:0 24px; white-space:nowrap; }
.cf-mq .cf-s1{color:color-mix(in oklch,var(--primary) 70%,transparent);}
.cf-mq .cf-s2{color:color-mix(in oklch,var(--secondary) 70%,transparent);}
.cf-center{ position:relative; z-index:10; flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:0 24px; margin-top:60px; width:100%; max-width:1024px; margin-left:auto; margin-right:auto; }
.cf-h2{ font-size:clamp(44px,8vw,104px); font-weight:900; letter-spacing:-.04em; margin-bottom:44px; text-align:center; }
.cf-links{ display:flex; flex-direction:column; align-items:center; gap:24px; width:100%; }
.cf-row{ display:flex; flex-wrap:wrap; justify-content:center; gap:16px; }
.cf-pill.cf-lg{ padding:18px 38px; border-radius:999px; color:var(--foreground); font-weight:700; font-size:15px; display:flex; align-items:center; gap:12px; }
.cf-pill.cf-sm{ padding:11px 22px; border-radius:999px; color:var(--muted-foreground); font-weight:500; font-size:13px; }
.cf-bottom{ position:relative; z-index:20; width:100%; padding:0 24px 32px; display:flex; align-items:flex-end; justify-content:space-between; gap:24px; }
@media(max-width:760px){ .cf-bottom{ flex-direction:column; align-items:center; } }
.cf-copyleft{ display:flex; flex-direction:column; gap:14px; }
.cf-copy{ color:var(--muted-foreground); font-size:11px; font-weight:600; letter-spacing:.15em; text-transform:uppercase; }
.cf-socials{ display:flex; gap:10px; }
.cf-social{ width:42px; height:42px; border-radius:999px; display:grid; place-items:center; color:var(--muted-foreground); }
.cf-social svg{ width:20px; height:20px; }
.cf-social:hover{ color:var(--foreground); }
.cf-made{ display:flex; align-items:center; gap:8px; padding:11px 22px; border-radius:999px; cursor:default; }
.cf-made span{ color:var(--muted-foreground); font-size:11px; font-weight:700; letter-spacing:.15em; text-transform:uppercase; }
.cf-made .cf-heart{ color:var(--destructive); font-size:15px; }
.cf-made .cf-brand{ color:var(--foreground); font-weight:900; font-size:13px; letter-spacing:0; }
.cf-top{ width:48px; height:48px; border-radius:999px; display:grid; place-items:center; color:var(--muted-foreground); }
.cf-top svg{ width:20px; height:20px; transition:transform .3s; }
.cf-top:hover svg{ transform:translateY(-6px); }
`;

const MARQUEE_ITEMS = [
  'Tracking server-side',
  'CAPI nativa',
  'Públicos automáticos',
  'Lookalike Negativo',
  'Instalação em 3 passos',
  'Integrações nativas',
  'Match Quality 9+',
];

function MarqueeContent() {
  return (
    <div className="cf-mq">
      {MARQUEE_ITEMS.map((item, i) => (
        <Fragment key={item}>
          <span>{item}</span>
          <span className={i % 2 === 0 ? 'cf-s1' : 'cf-s2'}>✦</span>
        </Fragment>
      ))}
    </div>
  );
}

interface MagneticButtonProps {
  as: 'a' | 'button';
  className: string;
  children: ReactNode;
  href?: string;
  target?: string;
  rel?: string;
  onClick?: () => void;
  ariaLabel?: string;
}

function MagneticButton({
  as,
  className,
  children,
  href,
  target,
  rel,
  onClick,
  ariaLabel,
}: MagneticButtonProps) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      const handleMove = (e: MouseEvent) => {
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        gsap.to(el, {
          x: x * 0.4,
          y: y * 0.4,
          rotationX: -y * 0.15,
          rotationY: x * 0.15,
          scale: 1.05,
          ease: 'power2.out',
          duration: 0.4,
          transformPerspective: 600,
        });
      };
      const handleLeave = () => {
        gsap.to(el, {
          x: 0,
          y: 0,
          rotationX: 0,
          rotationY: 0,
          scale: 1,
          ease: 'elastic.out(1, 0.3)',
          duration: 1.2,
        });
      };
      el.addEventListener('mousemove', handleMove);
      el.addEventListener('mouseleave', handleLeave);
      return () => {
        el.removeEventListener('mousemove', handleMove);
        el.removeEventListener('mouseleave', handleLeave);
      };
    }, el);
    return () => ctx.revert();
  }, []);

  if (as === 'a') {
    return (
      <a
        ref={(node) => {
          ref.current = node;
        }}
        href={href}
        target={target}
        rel={rel}
        aria-label={ariaLabel}
        className={className}
      >
        {children}
      </a>
    );
  }
  return (
    <button
      ref={(node) => {
        ref.current = node;
      }}
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={className}
    >
      {children}
    </button>
  );
}

export function CinematicFooter() {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const giantRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const linksRef = useRef<HTMLDivElement | null>(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    if (typeof window === 'undefined' || !wrapperRef.current) return;
    const ctx = gsap.context(() => {
      // "Trakd" gigante: parallax sutil ligado ao scroll (decorativo).
      gsap.fromTo(
        giantRef.current!,
        { y: '10vh', scale: 0.8, opacity: 0 },
        {
          y: '0vh',
          scale: 1,
          opacity: 1,
          ease: 'power1.out',
          scrollTrigger: {
            trigger: wrapperRef.current!,
            start: 'top 80%',
            end: 'bottom bottom',
            scrub: 1,
          },
        },
      );
      // Heading + pills: reveal ÚNICO que dispara assim que o footer começa a
      // entrar na viewport. Sem `scrub` (que deixava o conteúdo preso em
      // opacity:0 quando o ScrollTrigger media as posições antes do layout/fontes
      // assentarem). `once` + start cedo garante que, quando dá pra ver, já apareceu.
      gsap.from([headingRef.current!, linksRef.current!], {
        y: 50,
        opacity: 0,
        duration: 0.7,
        stagger: 0.12,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: wrapperRef.current!,
          start: 'top bottom-=80',
          once: true,
        },
      });
    }, wrapperRef);

    // Recalcula posições depois que fontes/imagens/layout assentam (a causa raiz
    // do reveal preso era o ScrollTrigger medir cedo demais).
    const refresh = () => ScrollTrigger.refresh();
    const tRefresh = window.setTimeout(refresh, 300);
    window.addEventListener('load', refresh);
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(refresh).catch(() => undefined);
    }

    // Rede de segurança: se por qualquer motivo o reveal não rodar e o footer já
    // estiver visível, força o estado final pra nunca ficar invisível.
    const tSafety = window.setTimeout(() => {
      const el = headingRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const inView = r.top < window.innerHeight && r.bottom > 0;
      const hidden = Number(getComputedStyle(el).opacity) < 0.05;
      if (inView && hidden) {
        gsap.set([headingRef.current, linksRef.current], { opacity: 1, y: 0 });
      }
    }, 1400);

    return () => {
      window.clearTimeout(tRefresh);
      window.clearTimeout(tSafety);
      window.removeEventListener('load', refresh);
      ctx.revert();
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />
      <div ref={wrapperRef} className="cf-wrap cf-curtain">
        <footer className="cf-footer">
          <div className="cf-aurora cf-breathe" />
          <div className="cf-grid" />

          <div ref={giantRef} className="cf-giant">
            Trakd
          </div>

          <div className="cf-marqueewrap">
            <div className="cf-marqueerow cf-marquee">
              <MarqueeContent />
              <MarqueeContent />
            </div>
          </div>

          <div className="cf-center">
            <h2 ref={headingRef} className="cf-h2 cf-glow">
              Pronto pra escalar?
            </h2>
            <div ref={linksRef} className="cf-links">
              <div className="cf-row">
                <MagneticButton as="a" href="/legal/termos" className="cf-pill cf-lg">
                  Termos de Uso
                </MagneticButton>
                <MagneticButton as="a" href="/legal/privacidade" className="cf-pill cf-lg">
                  Privacidade
                </MagneticButton>
                <MagneticButton as="a" href="/docs" className="cf-pill cf-lg">
                  Documentação
                </MagneticButton>
              </div>
              <div className="cf-row" style={{ marginTop: 8 }}>
                <MagneticButton as="a" href="/legal/lgpd" className="cf-pill cf-sm">
                  Compliance
                </MagneticButton>
                <MagneticButton as="a" href="/legal/exclusao-dados" className="cf-pill cf-sm">
                  Exclusão de Conta
                </MagneticButton>
                <MagneticButton as="a" href="/status" className="cf-pill cf-sm">
                  Status
                </MagneticButton>
              </div>
            </div>
          </div>

          <div className="cf-bottom">
            <div className="cf-copyleft">
              <div className="cf-copy">© {year} Trakd · Todos os direitos reservados</div>
              <div className="cf-socials">
                <MagneticButton
                  as="a"
                  href="https://instagram.com/trakd.pro"
                  target="_blank"
                  rel="noopener noreferrer"
                  ariaLabel="Instagram"
                  className="cf-pill cf-social"
                >
                  <AiFillInstagram />
                </MagneticButton>
                <MagneticButton
                  as="a"
                  href="https://x.com/trakdpro"
                  target="_blank"
                  rel="noopener noreferrer"
                  ariaLabel="X"
                  className="cf-pill cf-social"
                >
                  <FaSquareXTwitter />
                </MagneticButton>
                <MagneticButton
                  as="a"
                  href="https://www.linkedin.com/company/clareou-solucoes-digitais"
                  target="_blank"
                  rel="noopener noreferrer"
                  ariaLabel="LinkedIn"
                  className="cf-pill cf-social"
                >
                  <IoLogoLinkedin />
                </MagneticButton>
                <MagneticButton
                  as="a"
                  href="https://www.facebook.com/trakd.pro"
                  target="_blank"
                  rel="noopener noreferrer"
                  ariaLabel="Facebook"
                  className="cf-pill cf-social"
                >
                  <FaFacebookSquare />
                </MagneticButton>
                <MagneticButton
                  as="a"
                  href="https://www.youtube.com/@trakdpro"
                  target="_blank"
                  rel="noopener noreferrer"
                  ariaLabel="YouTube"
                  className="cf-pill cf-social"
                >
                  <AiFillYoutube />
                </MagneticButton>
              </div>
            </div>

            <div className="cf-pill cf-made">
              <span>Feito com</span>
              <span className="cf-heart cf-heartbeat">❤</span>
              <span>no</span>
              <span className="cf-brand">Brasil</span>
            </div>

            <MagneticButton
              as="button"
              onClick={scrollToTop}
              ariaLabel="Voltar ao topo"
              className="cf-pill cf-top"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 10l7-7m0 0l7 7m-7-7v18"
                />
              </svg>
            </MagneticButton>
          </div>
        </footer>
      </div>
    </>
  );
}
